import { z } from 'zod';
import { assertTypes, type NoNewFields } from '../../primitives';

/**
 * Square: a venue that runs Square POS takes OrderHub online orders without a second menu or
 * device. Square owns the menu and OrderHub mirrors it; online orders go to Square's Order Manager,
 * where staff accept or reject them. Gated by the `square-integration` feature flag. Business
 * rules: `invariants/SQUARE.md`.
 *
 * - One connection per venue: the Square seller it linked by OAuth, and the seller's location whose
 *   catalog it mirrors and that receives its orders.
 * - Tokens are stored as Square issues them, like Worldline's and Linkly's secrets.
 * - Every mirrored menu uuid is a UUID v5 of its Square object id, so each sync keeps the same
 *   uuids and carts, order snapshots and links stay valid.
 */

/** The Square object an OrderHub menu item or modifier came from, read when sending an order. */
export const squareCatalogLinkSchema = z.object({
  uuid: z.string(),
  squareObjectId: z.string(),
});
export type SquareCatalogLink = z.output<typeof squareCatalogLinkSchema>;

/**
 * OrderHub's copy of a Square image, so each image is copied once. By URL, not image id: an image
 * Square replaces keeps its id, with a new URL, and is copied again.
 */
export const squareImageLinkSchema = z.object({
  /** Where Square serves the image that was copied. */
  url: z.string(),
  cdnImageKey: z.string(),
});
export type SquareImageLink = z.output<typeof squareImageLinkSchema>;

/** A Square catalog object the menu leaves out, and why, for staff to fix in Square. */
export const squareSkippedObjectSchema = z.object({
  squareObjectId: z.string(),
  name: z.string(),
  reason: z.string(),
});
export type SquareSkippedObject = z.output<typeof squareSkippedObjectSchema>;

/** One of the seller's locations. */
export const squareLocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** ISO 4217, e.g. `AUD`: every amount sent to the location is in it. */
  currency: z.string(),
});
export type SquareLocation = z.output<typeof squareLocationSchema>;

export const squareConnectionSchema = z.object({
  restaurantId: z.string(),
  merchantId: z.string(),
  /** The location the venue mirrors. `null` until staff pick one of the seller's locations. */
  location: squareLocationSchema.nullable().default(null),
  accessToken: z.string(),
  accessTokenExpiresAt: z.number(),
  refreshToken: z.string(),
  catalogLinks: z.array(squareCatalogLinkSchema).default([]),
  imageLinks: z.array(squareImageLinkSchema).default([]),
  /** The menu sync scheduled to run next, so a burst of catalog edits syncs once. */
  menuSyncJobId: z.string().nullable().default(null),
  /** When the menu last synced without an error. */
  menuSyncedAt: z.number().nullable().default(null),
  /** Why the last menu sync failed. `null` once a sync succeeds. */
  menuSyncError: z.string().nullable().default(null),
  menuSyncSkipped: z.array(squareSkippedObjectSchema).default([]),
  /**
   * The venue's consumer-web hostnames Square verified for Apple Pay with the seller
   * (`activateApplePay`), e.g. `mycafe.com.au`. Apple Pay only runs on a verified hostname.
   */
  applePayHostnames: z.array(z.string()).default([]),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type SquareConnection = z.output<typeof squareConnectionSchema>;

/** Strict schema */
export const squareConnectionStrictSchema = squareConnectionSchema;

/** Convex schema */
export const squareConnectionCvxSchema = squareConnectionSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type SquareConnectionCvx = z.output<typeof squareConnectionCvxSchema>;
assertTypes<NoNewFields<SquareConnection, SquareConnectionCvx>>();

/** What admin-web sees: no tokens, and no links or sync job, which only the server reads. */
export const squareConnectionPublicCvxSchema = squareConnectionCvxSchema.omit({
  accessToken: true,
  refreshToken: true,
  catalogLinks: true,
  imageLinks: true,
  menuSyncJobId: true,
});
export type SquareConnectionPublicCvx = z.output<typeof squareConnectionPublicCvxSchema>;
